import { apiService, extractApiErrorMessage } from '../services/apiService';

export type ProxyAgentPlatform = 'windows' | 'mac-dmg' | 'mac-intel' | 'mac-arm' | 'linux';

/** User-facing product name for the local proxy installer (Windows / macOS / Linux). */
export const DESKTOP_AGENT_NAME = 'CRUD Expert desktop agent';

/** Bump when crud-expert-proxy-setup.exe is rebuilt so browsers do not reuse a stale download. */
export const WINDOWS_PROXY_INSTALLER_VERSION = '20260928b';
/** Bump when curd-expert-proxy.dmg is rebuilt (layout / installer UX). */
export const MAC_PROXY_INSTALLER_VERSION = '20260923a';

export function detectProxyAgentPlatform(): ProxyAgentPlatform {
  if (typeof navigator === 'undefined') return 'windows';
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  if (/Win/i.test(ua) || /Win/i.test(platform)) return 'windows';
  if (/Mac/i.test(ua) || /Mac/i.test(platform)) {
    // Apple Silicon vs Intel — default to DMG installer for simplicity.
    return 'mac-dmg';
  }
  return 'linux';
}

export function isWindowsProxyAgentPlatform(): boolean {
  return detectProxyAgentPlatform() === 'windows';
}

export function proxyPlatformLabel(platform: ProxyAgentPlatform): string {
  switch (platform) {
    case 'windows':
      return 'Windows';
    case 'mac-dmg':
      return 'macOS';
    case 'mac-intel':
      return 'macOS (Intel)';
    case 'mac-arm':
      return 'macOS (Apple Silicon)';
    case 'linux':
      return 'Linux';
    default:
      return 'your system';
  }
}

/** Walkthrough / setup copy — platform-specific installer steps (same files as downloadProxyAgent). */
export function desktopAgentInstallHint(platform: ProxyAgentPlatform): string {
  switch (platform) {
    case 'windows':
      return 'Run crud-expert-proxy-setup.exe, approve UAC, and finish the setup wizard — the CRUD Expert desktop agent starts in the background.';
    case 'mac-dmg':
      return 'Open curd-expert-proxy.dmg → double-click crud-expert-proxy.app (installs to Applications).';
    case 'mac-intel':
      return 'In Terminal: chmod +x curd-expert-proxy-mac-intel, then run ./curd-expert-proxy-mac-intel and keep it open.';
    case 'mac-arm':
      return 'In Terminal: chmod +x curd-expert-proxy-mac-arm, then run ./curd-expert-proxy-mac-arm and keep it open.';
    case 'linux':
      return 'Make curd-expert-proxy-linux executable, run it, and keep the terminal open.';
    default:
      return `Install and start the ${DESKTOP_AGENT_NAME} on http://localhost:9000.`;
  }
}

function triggerBrowserDownload(href: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** Download the CRUD Expert desktop agent for the given platform. */
export async function downloadProxyAgent(platform: ProxyAgentPlatform): Promise<void> {
  let fileName = '';
  switch (platform) {
    case 'windows':
      fileName = 'crud-expert-proxy-setup.exe';
      break;
    case 'mac-dmg':
      fileName = 'curd-expert-proxy.dmg';
      break;
    case 'mac-intel':
      fileName = 'curd-expert-proxy-mac-intel';
      break;
    case 'mac-arm':
      fileName = 'curd-expert-proxy-mac-arm';
      break;
    case 'linux':
      fileName = 'curd-expert-proxy-linux';
      break;
    default:
      return;
  }

  if (platform === 'windows') {
    triggerBrowserDownload(
      `/downloads/crud-expert-proxy-setup.exe?v=${WINDOWS_PROXY_INSTALLER_VERSION}`,
      'crud-expert-proxy-setup.exe'
    );
    return;
  }

  if (platform === 'mac-dmg') {
    // Static DMG from nginx — cache-bust so Downloads does not keep the old 3-file layout.
    triggerBrowserDownload(
      `/downloads/${fileName}?v=${MAC_PROXY_INSTALLER_VERSION}`,
      fileName
    );
    return;
  }

  try {
    await apiService.downloadLocalProxyArtifact(platform);
    return;
  } catch {
    /* fall through to static /downloads */
  }

  try {
    triggerBrowserDownload(`/downloads/${fileName}`, fileName);
  } catch (e) {
    window.alert(extractApiErrorMessage(e) || 'Download failed. Try again from Proxy Settings.');
  }
}
